import React, { useState } from 'react';
import Linkify from 'linkify-react';
import { Column, Row } from 'further-ui/components/Layout';
import Avatar from 'further-ui/components/Avatar';
import {
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
  Theme,
  styled,
} from '@mui/material';
import { Delete, ArrowForward } from '@mui/icons-material';
import { getQueryKey } from '@trpc/react-query';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';

const LoaderContainer = styled(Column)({
  height: '100%',
});

const NotesContainer = styled(Column)(({ theme }) => ({
  gap: theme.spacing(theme.spacingSizes.lg),
  flex: 1,
  justifyContent: 'space-between',
}));

const NoteItem = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'flipOrder',
})<{ flipOrder: boolean }>(({ theme, flipOrder }) => ({
  gap: theme.spacing(theme.spacingSizes.sm),
  alignItems: 'flex-start',
  width: '100%',
  flexDirection: flipOrder ? 'row-reverse' : 'row',
}));

const NoteContent = styled(Column)(({ theme }) => ({
  flex: 1,
  gap: theme.spacing(theme.spacingSizes.xs),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const LinkWithinNote = styled('a')(({ theme }: { theme: Theme }) => ({
  color: theme.palette.text.primary,
}));

const NoteText = styled(Typography)(() => ({
  whiteSpace: 'pre-line',
  wordBreak: 'break-word',
}));

const InputContainer = styled(Row)(({ theme }) => ({
  gap: theme.spacing(theme.spacingSizes.sm),
  alignItems: 'flex-start',
  maxWidth: 600,
  flex: 1,
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  padding: 0,
  fontSize: theme.fontSizes.regular,
  '& .MuiInputBase-input': {
    padding: 0,
  },
  alignItems: 'flex-start',
}));

const SubmitButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
  '&:disabled': {
    opacity: 0.5,
  },
}));

type Props = {
  vehicleId: string;
  vehicleApplicationId: string;
};

const Notes: React.FC<Props> = ({ vehicleId, vehicleApplicationId }) => {
  const [noteContent, setNoteContent] = useState('');
  const handleTrpcMutation = useHandleTrpcMutation();
  const user = useCurrentUser();
  const deleteDisclosure = useDisclosure<{ id: string }>();

  const queryKey = getQueryKey(
    api.vehicleApplication.listNotes,
    { vehicleApplicationId },
    'query',
  );

  const { data: notes, isFetching } = api.vehicleApplication.listNotes.useQuery(
    {
      vehicleId,
      vehicleApplicationId,
    },
  );

  const { mutate: createNote, isPending: isCreating } =
    api.vehicleApplication.createNote.useMutation();

  const { mutate: deleteNote, isPending: isDeleting } =
    api.vehicleApplication.deleteNote.useMutation();

  const currentUser = user.profile.data;

  const handleSubmit = () => {
    if (!noteContent.trim()) {
      return;
    }

    createNote(
      {
        vehicleId,
        vehicleApplicationId,
        content: noteContent.trim(),
      },
      handleTrpcMutation({
        successMessage: 'Note created successfully.',
        invalidations: [queryKey],
        onSuccess: () => {
          setNoteContent('');
        },
      }),
    );
  };

  const handleDelete = (noteId: string) => {
    deleteNote(
      { vehicleId, noteId },
      handleTrpcMutation({
        successMessage: 'Note deleted successfully.',
        invalidations: [queryKey],
      }),
    );
  };

  if (isFetching) {
    return (
      <LoaderContainer fullWidth centered justify="center">
        <CircularProgress />
      </LoaderContainer>
    );
  }

  return (
    <NotesContainer>
      <Column spacing="lg" fullWidth>
        {notes?.map((note) => {
          const createdByUser = note.createdByUser;
          const displayName = `${createdByUser.firstName} ${createdByUser.lastName}`;
          const profilePic = createdByUser.profilePic ?? '';
          const isOwnNote = note.createdByUser._id === currentUser?._id;

          return (
            <NoteItem key={note.id} flipOrder={!isOwnNote}>
              <Avatar
                color="random"
                text={displayName}
                src={profilePic}
                size="lg"
              />
              <NoteContent>
                <Typography variant="h6">{displayName}</Typography>
                <NoteText variant="body2" color="grey.900">
                  <Linkify
                    options={{
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      render: ({ attributes, content }) => (
                        <LinkWithinNote {...attributes}>
                          {content}
                        </LinkWithinNote>
                      ),
                    }}
                    as="span"
                  >
                    {note.content}
                  </Linkify>
                </NoteText>
                <Row justify="space-between" centered>
                  <Typography variant="body2" color="grey.600">
                    {note.createdAt?.toReadableWithTime()}
                  </Typography>
                  {isOwnNote ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        deleteDisclosure.stageAction({ id: note.id });
                      }}
                      disabled={isDeleting}
                    >
                      <Delete fontSize="small" htmlColor="#666" />
                    </IconButton>
                  ) : null}
                </Row>
              </NoteContent>
            </NoteItem>
          );
        })}
      </Column>

      <Row justify="center" fullWidth>
        <InputContainer>
          <Avatar
            color="random"
            text={
              currentUser
                ? `${currentUser.firstName} ${currentUser.lastName}`
                : ''
            }
            src={currentUser?.profilePic}
            size="lg"
          />
          <StyledInput
            placeholder="Add your note..."
            value={noteContent}
            onChange={(e) => {
              setNoteContent(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            multiline
            maxRows={4}
          />
          <SubmitButton
            onClick={handleSubmit}
            disabled={!noteContent.trim() || isCreating}
            color="primary"
          >
            {isCreating ? <CircularProgress size={24} /> : <ArrowForward />}
          </SubmitButton>
        </InputContainer>
      </Row>

      <ConfirmDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Delete note"
        content="Are you sure you wish to delete this note?"
        actions={{
          confirm: {
            label: 'Delete note',
            color: 'error',
            onClick: () => {
              handleDelete(deleteDisclosure.actionProps.id);
              deleteDisclosure.onClose();
            },
          },
        }}
      />
    </NotesContainer>
  );
};

export default Notes;
